import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// dayjsのプラグインを適用
dayjs.extend(utc);
dayjs.extend(timezone);

// グローバルに拡張メソッドを定義
declare global {
  interface Number {
    toComma(): string;
  }

  interface NumberConstructor {
    isNullOrUndefined(value: any): value is null | undefined;
  }

  interface String {
    toDateStringJP(): string;
    toDateTimeStringJP(): string;
    toDateString(): string;
    toDatetimeString(): string;
    toTimeString(): string;
    toDateField(): string;
    toDateTime(): Date | null;
    toISOStringJP(): string;
    toDayJs(): Dayjs | null;
  }
}

// 数値のカンマ区切り変換
Number.prototype.toComma = function (): string {
  return Number.isNullOrUndefined(this) ? "" : this.toLocaleString();
};

// nullまたはundefinedのチェック
Number.isNullOrUndefined = function (value: any): value is null | undefined {
  return value === null || value === undefined;
};

// 日付文字列を日本語表記に変換（YYYY年MM月DD日）
String.prototype.toDateStringJP = function (): string {
  const date = new Date(this.toString());
  if (isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${year}年${month}月${day}日`;
};

// 日付と時間を日本語表記に変換（YYYY年MM月DD日 HH:mm）
String.prototype.toDateTimeStringJP = function (): string {
  const date = new Date(this.toString());
  if (isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}年${month}月${day}日 ${hours}:${minutes}`;
};

// 日付をスラッシュ区切りに変換（YYYY/MM/DD）
String.prototype.toDateString = function (): string {
  const date = new Date(this.toString());
  if (isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${year}/${month}/${day}`;
};

// 日付と時間をスラッシュ区切りに変換（YYYY/MM/DD HH:mm）
String.prototype.toDatetimeString = function (): string {
  const date = new Date(this.toString());
  if (isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}/${month}/${day} ${hours}:${minutes}`;
};

// 時間のみを取得（HH:mm）
String.prototype.toTimeString = function (): string {
  const date = new Date(this.toString());
  if (isNaN(date.getTime())) return "";

  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${hours}:${minutes}`;
};

// 日付をフォーム用の文字列に変換（YYYY-MM-DD）
String.prototype.toDateField = function (): string {
  const date = new Date(this.toString());
  if (isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// 文字列を日付オブジェクトに変換
String.prototype.toDateTime = function (): Date | null {
  const date = new Date(this.toString());
  return isNaN(date.getTime()) ? null : date;
};

// 日本時間に基づいたISOフォーマットに変換
String.prototype.toISOStringJP = function (): string {
  const date = new Date(this.toString());
  if (isNaN(date.getTime())) return "";

  const jstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000); // UTC+9時間

  const year = jstDate.getUTCFullYear();
  const month = String(jstDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(jstDate.getUTCDate()).padStart(2, "0");
  const hours = String(jstDate.getUTCHours()).padStart(2, "0");
  const minutes = String(jstDate.getUTCMinutes()).padStart(2, "0");
  const seconds = String(jstDate.getUTCSeconds()).padStart(2, "0");
  const milliseconds = String(jstDate.getUTCMilliseconds()).padStart(3, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}+09:00`;
};

// 文字列をdayjsオブジェクトに変換
String.prototype.toDayJs = function (): Dayjs | null {
  if (!this) return null;
  const parsedDate = dayjs(this.toString());
  return parsedDate.isValid() ? parsedDate : null;
};

// Dayjsの型定義を拡張して新しいメソッドを追加
declare module "dayjs" {
  interface Dayjs {
    toFormattedString(): string;
    toISOStringJP(): string;
  }
}

// Dayjsのインスタンスに拡張メソッドを追加
dayjs.extend((_option, dayjsClass) => {
  dayjsClass.prototype.toFormattedString = function (): string {
    return this.format("YYYY-MM-DD");
  };
  
  dayjsClass.prototype.toISOStringJP = function (): string {
    return this.tz("Asia/Tokyo").format("YYYY-MM-DDTHH:mm:ss.SSSZ");
  };
});
